import React from 'react';
import {
  Paper,
  Typography,
  Box,
  
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Description as DescriptionIcon,
  LocationCity as LocationCityIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { ExecutiveSummary } from '../types/api.types';

interface ExecutiveSummaryCardProps {
  summary: ExecutiveSummary;
}

const ExecutiveSummaryCard: React.FC<ExecutiveSummaryCardProps> = ({ summary }) => {
  const sections = [
    {
      title: 'Property Overview',
      icon: <LocationCityIcon />,
      content: summary.property_overview,
      color: 'primary'
    },
    {
      title: 'Zoning Summary',
      icon: <DescriptionIcon />,
      content: summary.zoning_summary,
      color: 'secondary'
    },
    {
      title: 'Development Potential',
      icon: <TrendingUpIcon />,
      content: summary.development_potential,
      color: 'success'
    },
    {
      title: 'Financial Highlights',
      icon: <MoneyIcon />,
      content: summary.financial_highlights,
      color: 'warning'
    }
  ];

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon sx={{ color: 'primary.main' }} />
          Executive Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Key insights and recommendations for this property
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {sections.map((section, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', bgcolor: `${section.color}.light`, position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  p: 1,
                  borderRadius: '50%',
                  bgcolor: 'background.paper',
                  color: `${section.color}.main`
                }}
              >
                {section.icon}
              </Box>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: `${section.color}.dark`, fontWeight: 600 }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                  {section.content}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Recommendation Section */}
      <Box sx={{ 
        p: 3, 
        bgcolor: 'primary.main', 
        color: 'white',
        borderRadius: 2
      }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon />
          Primary Recommendation
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          {summary.recommendation}
        </Typography>
      </Box>

      {/* Quick Facts */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Decision Points
        </Typography>
        <Grid container spacing={2}>
          {[
            'Zoning compliant property',
            'Multiple development options available',
            'Strong market fundamentals',
            'Positive ROI projections'
          ].map((point, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2">{point}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
};

export default ExecutiveSummaryCard;